/**
 * Visual graph editor model (ADR-0049): pure spec <-> graph conversion and an editing reducer.
 *
 * The agent spec has no node positions; the editor seeds them from the SDK-compatible layered
 * layout (`toReactFlow`, LTR coordinates) and keeps manual moves in editor state only. Edges in
 * the editor are the union of explicit `graph.edges` and the edges implied by node fields
 * (router routes/default, parallel branches, human-approval `on_reject`), each with a stable id
 * that says where it lives in the spec so it can be edited or deleted.
 */
import { CLEAR_NODE, clearLayout } from "./clear";
import { withAnswerKeys } from "./datamap";
import { specEdges } from "./graph";
import type { AgentSpec, DecisionOption, NodeType, SpecEdge, SpecNode } from "./types";
import type { SpecIssue } from "./validate";

export const NODE_TYPES: readonly NodeType[] = [
  "llm",
  "tool",
  "router",
  "human_approval",
  "human_input",
  "parallel",
  "join",
  "subgraph",
  "retrieval",
  "python",
  "decision",
  "end",
  "wait",
  "calculate",
  "foreach",
  "table",
  "handover",
];
/** What the builder's palette offers: every type except built-in Python, which only runs when a
 * platform engineer allow-lists its entry point (users run code with the Code runner instead). */
export const PALETTE_TYPES: readonly NodeType[] = NODE_TYPES.filter((t) => t !== "python");

export type EdgeKind = "edge" | "route" | "default" | "branch" | "reject" | "low_confidence" | "limit" | "error" | "give_up" | "each";

/** A system step's "if it still fails, go to" step (ADR-0079); null for any other on_error. */
export function failPath(n: SpecNode): string | null {
  return n.type === "tool" && typeof n.on_error === "string" && n.on_error && n.on_error !== "fail" && n.on_error !== "continue" ? n.on_error : null;
}

export interface EditorEdge {
  /** `edge:<i>`, `route:<node>:<i>`, `default:<node>`, `branch:<node>:<i>`, `reject:<node>`, `low_confidence:<node>`, `limit:<node>`, `error:<node>`. */
  id: string;
  kind: EdgeKind;
  source: string;
  target: string;
  /** Condition shown on the edge (`when` for edges and routes, the kind otherwise). */
  label: string;
  /** Whether `when` can be edited (explicit edges and router routes). */
  conditional: boolean;
  when: string | null;
}

export interface Position {
  x: number;
  y: number;
}

export type Selection = { kind: "node"; id: string } | { kind: "edge"; id: string } | null;

export interface EditorState {
  spec: AgentSpec;
  /** LTR layout coordinates per node id (mirrored at render time for RTL). */
  positions: Record<string, Position>;
  selection: Selection;
  past: AgentSpec[];
  future: AgentSpec[];
  /** The spec as loaded (or last saved): `dirty` compares against it. */
  baseline: AgentSpec;
  /** Measured step heights on screen (ADR-0071). */
  heights?: Record<string, number>;
  /** A step was dragged: keep `positions` instead of the automatic layout. */
  manual?: boolean;
  /** A plain message about the last arrow action (shown above the canvas until the next edit). */
  notice?: EditorNotice | null;
}

/**
 * Why an arrow action did nothing: `duplicate` (the arrow already exists and is now selected),
 * `retargetDuplicate` (an arrow from the same step already goes there) or `retargetSelf` (an
 * arrow can't go back into the step it leaves). `source`/`target` are step ids.
 */
export interface EditorNotice {
  code: "duplicate" | "retargetDuplicate" | "retargetSelf";
  source: string;
  target: string;
}

const HISTORY = 100;

export function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

/** All edges of the spec as the editor shows them. */
export function editorEdges(spec: AgentSpec): EditorEdge[] {
  const g = spec.spec.graph;
  const out: EditorEdge[] = [];
  (g.edges ?? []).forEach((e, i) =>
    out.push({ id: `edge:${i}`, kind: "edge", source: e.from, target: e.to, label: e.when ?? "", conditional: true, when: e.when ?? null }),
  );
  for (const n of g.nodes) {
    if (n.type === "router") {
      (n.routes ?? []).forEach((r, i) =>
        out.push({ id: `route:${n.id}:${i}`, kind: "route", source: n.id, target: r.to, label: r.when, conditional: true, when: r.when }),
      );
      if (n.default) out.push({ id: `default:${n.id}`, kind: "default", source: n.id, target: n.default, label: "default", conditional: false, when: null });
    } else if (n.type === "parallel") {
      (n.branches ?? []).forEach((b, i) =>
        out.push({ id: `branch:${n.id}:${i}`, kind: "branch", source: n.id, target: b, label: "parallel", conditional: false, when: null }),
      );
    } else if (n.type === "human_approval" && n.on_reject) {
      out.push({ id: `reject:${n.id}`, kind: "reject", source: n.id, target: n.on_reject, label: "rejected", conditional: false, when: null });
    } else if (n.type === "decision" && n.on_low_confidence?.route) {
      out.push({ id: `low_confidence:${n.id}`, kind: "low_confidence", source: n.id, target: n.on_low_confidence.route, label: "low_confidence", conditional: false, when: null });
    }
    // Any step with a repeat limit may say where to go once the limit is reached (on_limit).
    if (n.on_limit) out.push({ id: `limit:${n.id}`, kind: "limit", source: n.id, target: n.on_limit, label: "limit", conditional: false, when: null });
    const fails = failPath(n);
    if (fails) out.push({ id: `error:${n.id}`, kind: "error", source: n.id, target: fails, label: "error", conditional: false, when: null });
    // A "Wait for" step's "if nothing comes in time, go to" (ADR-0094).
    if (n.type === "wait" && n.on_give_up) out.push({ id: `give_up:${n.id}`, kind: "give_up", source: n.id, target: n.on_give_up, label: "give_up", conditional: false, when: null });
    // A for-each step's "for each item" arrow to the first of its steps (ADR-0104).
    const first = n.type === "foreach" ? n.steps?.[0] : undefined;
    if (first) out.push({ id: `each:${n.id}`, kind: "each", source: n.id, target: first, label: "each", conditional: false, when: null });
  }
  return out;
}

// ---------------------------------------------------------------- decision nodes (ADR-0059)

const YES_NO: DecisionOption[] = [
  { value: "yes", label: { en: "Yes", ar: "نعم" } },
  { value: "no", label: { en: "No", ar: "لا" } },
];

/** The answers a decision may give: declared options, yes/no, or the rubric anchors. */
export function decisionOptions(node: SpecNode): DecisionOption[] {
  if (node.type !== "decision") return [];
  if (node.kind === "boolean") return node.options?.length ? node.options : YES_NO;
  if (node.kind === "score") return (node.rubric?.anchors ?? []).map((a) => ({ value: String(a.value), label: a.label }));
  return node.options ?? [];
}

/** Whether edges leaving this node name an option in `when` (choice and yes/no decisions). */
export function routesByOption(node: SpecNode | undefined): boolean {
  return node?.type === "decision" && node.kind !== "score";
}

/** Switches a decision's kind, resetting the fields that do not apply to the new kind. */
export function decisionKindPatch(kind: "choice" | "boolean" | "score"): Partial<SpecNode> {
  if (kind === "boolean") return { kind, options: undefined, rubric: undefined };
  if (kind === "score") {
    return {
      kind,
      options: undefined,
      rubric: {
        min: 1,
        max: 5,
        anchors: [
          { value: 1, label: { en: "Low", ar: "منخفض" } },
          { value: 3, label: { en: "Medium", ar: "متوسط" } },
          { value: 5, label: { en: "High", ar: "مرتفع" } },
        ],
      },
    };
  }
  return { kind, rubric: undefined, options: defaultDecisionOptions() };
}

function defaultDecisionOptions(): DecisionOption[] {
  return [
    { value: "urgent", label: { en: "Urgent", ar: "عاجل" } },
    { value: "normal", label: { en: "Normal", ar: "عادي" } },
  ];
}

/** Initial positions: the clear top-to-bottom layout (ADR-0071), LTR coordinates. */
export function autoLayout(spec: AgentSpec, heights: Record<string, number> = {}): Record<string, Position> {
  return clearLayout(spec, heights);
}

/** A position for a new node: one row below the lowest node. */
export function nextPosition(positions: Record<string, Position>): Position {
  const all = Object.values(positions);
  if (!all.length) return { x: 0, y: 0 };
  return { x: 0, y: Math.max(...all.map((p) => p.y)) + CLEAR_NODE.height + CLEAR_NODE.gapY };
}

/** A fresh agent: an entry LLM node followed by an end node. */
export function blankSpec(tenant: string, owner: string): AgentSpec {
  return {
    apiVersion: "agents.t-ceratops.io/v1",
    kind: "Agent",
    metadata: {
      name: "new-agent",
      tenant,
      version: "0.1.0",
      owner: owner || "unassigned",
      description: { en: "New agent", ar: "وكيل جديد" },
    },
    spec: {
      role: { en: "Assistant", ar: "مساعد" },
      goals: [{ en: "Help the user", ar: "مساعدة المستخدم" }],
      pattern: "custom",
      model: { alias: "chat-default" },
      tools: [],
      knowledge: [],
      risk_tier: 1,
      permissions: { max_classification: "internal" },
      graph: {
        entry: "agent",
        nodes: [
          { id: "agent", type: "llm", prompt: { template: "Answer the question: {question}", variables: ["question"] }, tools: [] },
          { id: "done", type: "end" },
        ],
        edges: [{ from: "agent", to: "done" }],
      },
    },
  };
}

/**
 * Where a new route of a rule step ("Add route") goes at first (G-camp-fin-21): the step's
 * "otherwise" path (the step after it), else an end step, never back to the first step (that
 * made a loop). The builder picks the real target next.
 */
export function newRouteTarget(spec: AgentSpec, router: SpecNode): string {
  const ids = new Set(spec.spec.graph.nodes.map((n) => n.id));
  if (router.default && router.default !== router.id && ids.has(router.default)) return router.default;
  return spec.spec.graph.nodes.find((n) => n.type === "end")?.id ?? "";
}

/**
 * "New agent (blank)" (G-camp-fin-20): only its start and its end, nothing that answers yet, so
 * no leftover AI step's answer becomes the agent's final answer. Steps are added from there.
 */
export function newAgentSpec(tenant: string, owner: string): AgentSpec {
  const s = blankSpec(tenant, owner);
  return { ...s, spec: { ...s.spec, graph: { entry: "done", nodes: [{ id: "done", type: "end" }], edges: [] } } };
}

/** Default fields for a node of the given type (enough to pass the JSON schema where possible). */
export function defaultNode(type: NodeType, id: string): SpecNode {
  switch (type) {
    case "llm":
      return { id, type, prompt: { template: "", variables: [] }, tools: [] };
    case "tool":
      return { id, type, tool: "", action: "", args: {} };
    case "router":
      return { id, type, routes: [], default: "" };
    case "human_approval":
      return { id, type, subject_key: "", approver_roles: ["approver"], sla: "PT4H", on_reject: null };
    case "human_input":
      return { id, type, question: { en: "", ar: "" }, output_key: "answer" };
    case "parallel":
      return { id, type, branches: [], join: "" };
    case "join":
      return { id, type, strategy: "merge" };
    case "subgraph":
      return { id, type, agent: "", input_map: {}, output_key: "subgraph_result" };
    case "retrieval":
      return { id, type, collection: "", query: "{{ state.question }}", output_key: "context" };
    case "python":
      return { id, type, entrypoint: "" };
    case "decision":
      return {
        id,
        type,
        question: { en: "", ar: "" },
        kind: "choice",
        options: defaultDecisionOptions(),
        input: "{{ state.question }}",
        threshold: 0.7,
        on_low_confidence: { ask: { approver_roles: ["approver"] } },
      };
    case "end":
      return { id, type };
    case "wait":
      return { id, type, after: "PT1H" };
    case "calculate":
      return { id, type, values: [{ key: "result", expr: "0" }] };
    case "foreach":
      return { id, type, items: "", steps: [], concurrency: 4, max_items: 100, on_item_error: "skip" };
    case "table":
      return { id, type, op: "read" };
    case "handover":
      return { id, type };
  }
}

export function uniqueId(spec: AgentSpec, base: string): string {
  const ids = new Set(spec.spec.graph.nodes.map((n) => n.id));
  if (!ids.has(base)) return base;
  for (let i = 1; ; i++) if (!ids.has(`${base}_${i}`)) return `${base}_${i}`;
}

// ---------------------------------------------------------------- pure spec operations

function mapNodes(spec: AgentSpec, fn: (n: SpecNode) => SpecNode): AgentSpec {
  return { ...spec, spec: { ...spec.spec, graph: { ...spec.spec.graph, nodes: spec.spec.graph.nodes.map(fn) } } };
}
function withGraph(spec: AgentSpec, patch: Partial<AgentSpec["spec"]["graph"]>): AgentSpec {
  return { ...spec, spec: { ...spec.spec, graph: { ...spec.spec.graph, ...patch } } };
}

export function addNode(spec: AgentSpec, type: NodeType, id?: string): { spec: AgentSpec; id: string } {
  const nid = uniqueId(spec, id ?? type);
  const g = spec.spec.graph;
  return { spec: withGraph(spec, { nodes: [...g.nodes, defaultNode(type, nid)], entry: g.nodes.length ? g.entry : nid }), id: nid };
}

/** Removes a node and every reference to it (edges, routes, branches, joins, on_reject, entry). */
export function removeNode(spec: AgentSpec, id: string): AgentSpec {
  const g = spec.spec.graph;
  if (!g.nodes.some((n) => n.id === id)) return spec;
  const nodes = g.nodes
    .filter((n) => n.id !== id)
    .map((n) => {
      const c = { ...n };
      if (c.routes) c.routes = c.routes.filter((r) => r.to !== id);
      if (c.default === id) c.default = "";
      if (c.branches) c.branches = c.branches.filter((b) => b !== id);
      if (c.join === id) c.join = "";
      if (c.on_reject === id) c.on_reject = null;
      if (c.on_low_confidence?.route === id) c.on_low_confidence = { ask: { approver_roles: ["approver"] } };
      if (c.on_limit === id) delete c.on_limit;
      if (failPath(c) === id) delete c.on_error;
      return c;
    });
  const edges = (g.edges ?? []).filter((e) => e.from !== id && e.to !== id);
  const entry = g.entry === id ? (nodes[0]?.id ?? "") : g.entry;
  return withGraph(spec, { nodes, edges, entry });
}

/**
 * The one step a step goes on to, when deleting it can reconnect the arrows into it there
 * (G-flow-13); null when it goes nowhere, to several steps, or its arrows come back to it.
 */
export function reconnectTarget(spec: AgentSpec, id: string): string | null {
  const out = [...new Set(editorEdges(spec).filter((e) => e.source === id).map((e) => e.target))].filter((t) => t !== id);
  const next = out.length === 1 ? (out[0] as string) : null;
  // Only arrows from other steps can be reconnected (one from the next step would loop on it).
  const into = editorEdges(spec).filter((e) => e.target === id && e.source !== id && e.source !== next);
  return next && into.length ? next : null;
}

/**
 * Deletes a step and points every arrow into it at the step it went on to (keeping each arrow's
 * condition and label). An arrow whose step already goes there, or that would come back into
 * its own step, is dropped with the deleted step.
 */
export function removeNodeReconnect(spec: AgentSpec, id: string): AgentSpec {
  const next = reconnectTarget(spec, id);
  if (!next) return removeNode(spec, id);
  let out = spec;
  for (const e of editorEdges(spec).filter((x) => x.target === id && x.source !== id)) {
    const r = retarget(out, e.id, next);
    if (r.ok) out = r.spec;
  }
  // The graph started at the deleted step: it now starts where that step went on to.
  if (out.spec.graph.entry === id) out = withGraph(out, { entry: next });
  return removeNode(out, id);
}

/** Renames a node and rewrites every reference so edges stay consistent. */
export function renameNode(spec: AgentSpec, from: string, to: string): AgentSpec {
  if (from === to || !to || spec.spec.graph.nodes.some((n) => n.id === to)) return spec;
  const r = (x: string) => (x === from ? to : x);
  const g = spec.spec.graph;
  const nodes = g.nodes.map((n) => {
    const c = { ...n, id: r(n.id) };
    if (c.routes) c.routes = c.routes.map((x) => ({ ...x, to: r(x.to) }));
    if (typeof c.default === "string") c.default = r(c.default);
    if (c.branches) c.branches = c.branches.map(r);
    if (typeof c.join === "string") c.join = r(c.join);
    if (typeof c.on_reject === "string") c.on_reject = r(c.on_reject);
    if (c.on_low_confidence?.route) c.on_low_confidence = { route: r(c.on_low_confidence.route) };
    if (typeof c.on_limit === "string") c.on_limit = r(c.on_limit);
    const fails = failPath(c);
    if (fails) c.on_error = r(fails);
    return c;
  });
  const edges = (g.edges ?? []).map((e) => ({ ...e, from: r(e.from), to: r(e.to) }));
  return withGraph(spec, { nodes, edges, entry: r(g.entry) });
}

/** The editor edge from `source` to `target`, if there is one. */
export function findEdge(spec: AgentSpec, source: string, target: string): EditorEdge | undefined {
  return editorEdges(spec).find((e) => e.source === source && e.target === target);
}

/**
 * Connects two nodes. Routers get a route (or their default when they have none), parallel nodes
 * a branch; every other node gets an explicit edge. Duplicates are ignored here (the reducer
 * selects the existing arrow and says so).
 */
export function connect(spec: AgentSpec, source: string, target: string): AgentSpec {
  if (source === target) return spec;
  const g = spec.spec.graph;
  const src = g.nodes.find((n) => n.id === source);
  if (!src || !g.nodes.some((n) => n.id === target)) return spec;
  if (specEdges(spec).some(([s, t]) => s === source && t === target)) return spec;
  if (src.type === "router") {
    return mapNodes(spec, (n) =>
      n.id !== source ? n : !n.default ? { ...n, default: target } : { ...n, routes: [...(n.routes ?? []), { when: `state.route == "${target}"`, to: target }] },
    );
  }
  if (src.type === "parallel") return mapNodes(spec, (n) => (n.id === source ? { ...n, branches: [...(n.branches ?? []), target] } : n));
  const edge: SpecEdge = { from: source, to: target };
  if (routesByOption(src)) {
    // A decision edge is for one option: take the first option that has no edge yet.
    const used = new Set((g.edges ?? []).filter((e) => e.from === source).map((e) => e.when));
    const free = decisionOptions(src).find((o) => !used.has(o.value));
    if (free) edge.when = free.value;
  }
  return withGraph(spec, { edges: [...(g.edges ?? []), edge] });
}

function parseEdgeId(id: string): { kind: EdgeKind; node: string; index: number } {
  const [kind, a, b] = id.split(":") as [EdgeKind, string, string | undefined];
  if (kind === "edge") return { kind, node: "", index: Number(a) };
  return { kind, node: a, index: b === undefined ? -1 : Number(b) };
}

export function removeEdge(spec: AgentSpec, edgeId: string): AgentSpec {
  const { kind, node, index } = parseEdgeId(edgeId);
  if (kind === "edge") return withGraph(spec, { edges: (spec.spec.graph.edges ?? []).filter((_, i) => i !== index) });
  return mapNodes(spec, (n) => {
    if (n.id !== node) return n;
    if (kind === "route") return { ...n, routes: (n.routes ?? []).filter((_, i) => i !== index) };
    if (kind === "default") return { ...n, default: "" };
    if (kind === "branch") return { ...n, branches: (n.branches ?? []).filter((_, i) => i !== index) };
    if (kind === "low_confidence") return { ...n, on_low_confidence: { ask: { approver_roles: ["approver"] } } };
    if (kind === "limit") {
      const { on_limit: _drop, ...rest } = n;
      return rest as SpecNode;
    }
    if (kind === "error") {
      const { on_error: _drop, ...rest } = n;
      return rest as SpecNode;
    }
    if (kind === "give_up") {
      const { on_give_up: _drop, ...rest } = n;
      return rest as SpecNode;
    }
    // "For each item" (ADR-0104): the first step is no longer done for each item.
    if (kind === "each") return { ...n, steps: (n.steps ?? []).slice(1) };
    return { ...n, on_reject: null };
  });
}

export type RetargetResult = { ok: true; spec: AgentSpec } | { ok: false; reason: "missing" | "self" | "duplicate" };

/**
 * Points an arrow at another step, keeping where it lives in the spec (so its id), its condition
 * and its label. Refuses an arrow into its own step, a step that doesn't exist and a second arrow
 * between the same two steps.
 */
export function retarget(spec: AgentSpec, edgeId: string, target: string): RetargetResult {
  const edge = editorEdges(spec).find((e) => e.id === edgeId);
  if (!edge || !spec.spec.graph.nodes.some((n) => n.id === target)) return { ok: false, reason: "missing" };
  if (edge.target === target) return { ok: true, spec };
  if (edge.source === target) return { ok: false, reason: "self" };
  if (specEdges(spec).some(([s, t]) => s === edge.source && t === target)) return { ok: false, reason: "duplicate" };
  const { kind, node, index } = parseEdgeId(edgeId);
  if (kind === "edge") {
    return { ok: true, spec: withGraph(spec, { edges: (spec.spec.graph.edges ?? []).map((e, i) => (i === index ? { ...e, to: target } : e)) }) };
  }
  const next = mapNodes(spec, (n) => {
    if (n.id !== node) return n;
    if (kind === "route") return { ...n, routes: (n.routes ?? []).map((r, i) => (i === index ? { ...r, to: target } : r)) };
    if (kind === "default") return { ...n, default: target };
    if (kind === "branch") return { ...n, branches: (n.branches ?? []).map((b, i) => (i === index ? target : b)) };
    if (kind === "low_confidence") return { ...n, on_low_confidence: { route: target } };
    if (kind === "limit") return { ...n, on_limit: target };
    if (kind === "error") return { ...n, on_error: target };
    if (kind === "give_up") return { ...n, on_give_up: target };
    if (kind === "each") return { ...n, steps: [target, ...(n.steps ?? []).filter((s, i) => i > 0 && s !== target)] };
    return { ...n, on_reject: target };
  });
  return { ok: true, spec: next };
}

/** Sets the condition of an explicit edge or a router route (empty clears an edge's `when`). */
export function setEdgeWhen(spec: AgentSpec, edgeId: string, when: string): AgentSpec {
  const { kind, node, index } = parseEdgeId(edgeId);
  if (kind === "edge") {
    return withGraph(spec, {
      edges: (spec.spec.graph.edges ?? []).map((e, i) => {
        if (i !== index) return e;
        const { when: _drop, ...rest } = e;
        return when ? { ...rest, when } : rest;
      }),
    });
  }
  if (kind === "route") {
    return mapNodes(spec, (n) => (n.id === node ? { ...n, routes: (n.routes ?? []).map((r, i) => (i === index ? { ...r, when } : r)) } : n));
  }
  return spec;
}

/** Merges fields into a node; `undefined` values remove the field. Renames go through renameNode. */
export function updateNode(spec: AgentSpec, id: string, patch: Partial<SpecNode>): AgentSpec {
  return mapNodes(spec, (n) => {
    if (n.id !== id) return n;
    const out: SpecNode = { ...n };
    for (const [k, v] of Object.entries(patch)) {
      if (k === "id" || k === "type") continue;
      if (v === undefined) delete out[k];
      else out[k] = v;
    }
    return out;
  });
}

export function setEntry(spec: AgentSpec, id: string): AgentSpec {
  return spec.spec.graph.nodes.some((n) => n.id === id) ? withGraph(spec, { entry: id }) : spec;
}

/** Grants `tool.action` in spec.tools if missing (tool nodes need a grant to validate). */
export function ensureGrant(spec: AgentSpec, tool: string, action: string): AgentSpec {
  if (!tool || !action) return spec;
  const tools = spec.spec.tools ?? [];
  const g = tools.find((x) => x.ref === tool);
  if (g?.actions.includes(action)) return spec;
  const next = g ? tools.map((x) => (x.ref === tool ? { ...x, actions: [...x.actions, action] } : x)) : [...tools, { ref: tool, actions: [action] }];
  return { ...spec, spec: { ...spec.spec, tools: next } };
}

/** Declares a knowledge collection if missing (retrieval nodes need it to validate). */
export function ensureKnowledge(spec: AgentSpec, collection: string): AgentSpec {
  if (!collection) return spec;
  const k = spec.spec.knowledge ?? [];
  if (k.some((x) => x.collection === collection)) return spec;
  return { ...spec, spec: { ...spec.spec, knowledge: [...k, { collection, top_k: 8 }] } };
}

/** Bumps the patch (or pre-release-free patch) part of a semantic version. */
export function bumpVersion(v: string): string {
  const m = /^(\d+)\.(\d+)\.(\d+)/.exec(v);
  return m ? `${m[1]}.${m[2]}.${Number(m[3]) + 1}` : "0.1.0";
}

// ---------------------------------------------------------------- validation → graph

/** Node ids an issue refers to (schema paths by index, semantic messages by node/edge id). */
export function issueNodeIds(spec: AgentSpec, issue: SpecIssue): string[] {
  const nodes = spec.spec.graph.nodes;
  const m = /^\/spec\/graph\/nodes\/(\d+)/.exec(issue.path);
  if (m) {
    const n = nodes[Number(m[1])];
    return n ? [n.id] : [];
  }
  if (issue.path === "/spec/graph/entry") return [];
  if (!issue.path.startsWith("/spec/graph")) return [];
  const words = new Set(issue.message.split(/[\s,:]+|->/).filter(Boolean));
  return nodes.map((n) => n.id).filter((id) => words.has(id));
}

export function invalidNodeIds(spec: AgentSpec, issues: SpecIssue[]): Set<string> {
  return new Set(issues.flatMap((i) => issueNodeIds(spec, i)));
}

// ---------------------------------------------------------------- reducer

export type EditorAction =
  | { type: "load"; spec: AgentSpec }
  /** Positions kept from an earlier session (dragged steps), applied when they cover the graph. */
  | { type: "restore"; positions: Record<string, Position> }
  /** Measured step heights (the clear layout follows them until a step is dragged). */
  | { type: "measure"; heights: Record<string, number> }
  | { type: "saved"; spec: AgentSpec }
  | { type: "add"; nodeType: NodeType; position?: Position }
  | { type: "removeSelected" }
  /** `reconnect`: arrows into the step go on to its next step instead (G-flow-13). */
  | { type: "removeNode"; id: string; reconnect?: boolean }
  | { type: "removeEdge"; id: string }
  | { type: "rename"; from: string; to: string }
  | { type: "connect"; source: string; target: string }
  /** Points an arrow at another step (the "Goes to" choice, or dragging its end on the canvas). */
  | { type: "retarget"; id: string; target: string }
  /** Puts away the message about the last arrow action. */
  | { type: "dismissNotice" }
  | { type: "updateNode"; id: string; patch: Partial<SpecNode> }
  | { type: "setTool"; id: string; tool: string; action: string }
  | { type: "setCollection"; id: string; collection: string }
  | { type: "setWhen"; id: string; when: string }
  | { type: "setEntry"; id: string }
  | { type: "updateSpec"; fn: (s: AgentSpec) => AgentSpec }
  | { type: "move"; id: string; position: Position }
  | { type: "layout" }
  | { type: "select"; selection: Selection }
  | { type: "undo" }
  | { type: "redo" };

export function initEditor(spec: AgentSpec): EditorState {
  const s = clone(spec);
  return { spec: s, positions: autoLayout(s), selection: null, past: [], future: [], baseline: s };
}

function commit(state: EditorState, next: AgentSpec, extra: Partial<EditorState> = {}): EditorState {
  if (next === state.spec) return { ...state, notice: null, ...extra };
  // A later step that uses an AI step's plain answer makes that step keep it (G-io-19).
  const spec = withAnswerKeys(next);
  return { ...state, notice: null, ...extra, spec, past: [...state.past, state.spec].slice(-HISTORY), future: [] };
}

function selectionExists(spec: AgentSpec, sel: Selection): Selection {
  if (!sel) return null;
  if (sel.kind === "node") return spec.spec.graph.nodes.some((n) => n.id === sel.id) ? sel : null;
  return editorEdges(spec).some((e) => e.id === sel.id) ? sel : null;
}

export function editorReducer(state: EditorState, a: EditorAction): EditorState {
  switch (a.type) {
    case "load":
      return initEditor(a.spec);
    case "saved":
      return { ...state, baseline: a.spec };
    case "add": {
      const r = addNode(state.spec, a.nodeType);
      const positions = { ...state.positions, [r.id]: a.position ?? nextPosition(state.positions) };
      return commit(state, r.spec, { positions, selection: { kind: "node", id: r.id } });
    }
    case "removeSelected": {
      const sel = state.selection;
      if (!sel) return state;
      return editorReducer(state, sel.kind === "node" ? { type: "removeNode", id: sel.id } : { type: "removeEdge", id: sel.id });
    }
    case "removeNode":
      return commit(state, a.reconnect ? removeNodeReconnect(state.spec, a.id) : removeNode(state.spec, a.id), { selection: null });
    case "removeEdge":
      return commit(state, removeEdge(state.spec, a.id), { selection: null });
    case "rename": {
      const spec = renameNode(state.spec, a.from, a.to);
      if (spec === state.spec) return state;
      const { [a.from]: pos, ...rest } = state.positions;
      return commit(state, spec, { positions: pos ? { ...rest, [a.to]: pos } : rest, selection: { kind: "node", id: a.to } });
    }
    case "connect": {
      const existing = a.source === a.target ? undefined : findEdge(state.spec, a.source, a.target);
      // The arrow is already there: select it so it can be changed, and say so.
      if (existing) return { ...state, selection: { kind: "edge", id: existing.id }, notice: { code: "duplicate", source: a.source, target: a.target } };
      return commit(state, connect(state.spec, a.source, a.target));
    }
    case "retarget": {
      const edge = editorEdges(state.spec).find((e) => e.id === a.id);
      const r = retarget(state.spec, a.id, a.target);
      if (r.ok) return commit(state, r.spec, { selection: { kind: "edge", id: a.id } });
      if (!edge || r.reason === "missing") return state;
      return { ...state, notice: { code: r.reason === "self" ? "retargetSelf" : "retargetDuplicate", source: edge.source, target: a.target } };
    }
    case "updateNode":
      return commit(state, updateNode(state.spec, a.id, a.patch));
    case "setTool":
      return commit(state, ensureGrant(updateNode(state.spec, a.id, { tool: a.tool, action: a.action }), a.tool, a.action));
    case "setCollection":
      return commit(state, ensureKnowledge(updateNode(state.spec, a.id, { collection: a.collection }), a.collection));
    case "setWhen":
      return commit(state, setEdgeWhen(state.spec, a.id, a.when));
    case "setEntry":
      return commit(state, setEntry(state.spec, a.id));
    case "updateSpec":
      return commit(state, a.fn(state.spec));
    case "move": {
      // The first drag freezes the automatic layout where it is.
      const base = state.manual ? state.positions : autoLayout(state.spec, state.heights ?? {});
      return { ...state, manual: true, positions: { ...base, [a.id]: a.position } };
    }
    case "restore": {
      const ids = state.spec.spec.graph.nodes.map((n) => n.id);
      const ok = ids.every((id) => Number.isFinite(a.positions[id]?.x) && Number.isFinite(a.positions[id]?.y));
      return ok ? { ...state, manual: true, positions: Object.fromEntries(ids.map((id) => [id, a.positions[id] as Position])) } : state;
    }
    case "dismissNotice":
      return state.notice ? { ...state, notice: null } : state;
    case "measure":
      return { ...state, heights: { ...(state.heights ?? {}), ...a.heights } };
    case "layout":
      return { ...state, manual: false, positions: autoLayout(state.spec, state.heights ?? {}) };
    case "select": {
      // Picking something else puts away the last arrow message.
      const same = JSON.stringify(a.selection) === JSON.stringify(state.selection);
      return { ...state, selection: a.selection, notice: same ? state.notice : null };
    }
    case "undo": {
      const prev = state.past[state.past.length - 1];
      if (!prev) return state;
      return { ...state, notice: null, spec: prev, past: state.past.slice(0, -1), future: [state.spec, ...state.future], selection: selectionExists(prev, state.selection) };
    }
    case "redo": {
      const next = state.future[0];
      if (!next) return state;
      return { ...state, notice: null, spec: next, past: [...state.past, state.spec], future: state.future.slice(1), selection: selectionExists(next, state.selection) };
    }
  }
}

export function isDirty(state: EditorState): boolean {
  return JSON.stringify(state.spec) !== JSON.stringify(state.baseline);
}
